<template>
  <div 
    class="magnetic-carousel-wrapper"
    @wheel="handleScroll"
    @touchstart="handleTouchStart"
    @touchmove="handleTouchMove"
    @touchend="handleTouchEnd"
    ref="carouselWrapper"
  >
    <div class="carousel-container">
      <button
        class="carousel-arrow carousel-arrow-left"
        @click="handleArrowClick('prev')"
        @mouseenter="startContinuousNavigation('prev')"
        @mouseleave="stopContinuousNavigation"
        :disabled="centerIndex === 0"
        v-if="cards.length > 1"
      >
        <i class="fas fa-chevron-left"></i>
      </button>

      <div class="carousel-cards-container" ref="carouselScroll">
        <div
          v-for="(card, index) in cards"
          :key="card.id || index"
          class="carousel-card-wrapper"
          :class="getCardPosition(index)"
          @click="setCenterCard(index)"
        >
          <slot name="card" :card="card" :index="index">
            <ServiceCarouselCard
              :service-name="card.service_name || card.name"
              :icon-class="card.icon_class || 'fas fa-check-circle'"
              :is-selected="card.is_selected"
              :description="card.description || ''"
              @toggle="(checked) => $emit('toggle-service', card, checked)"
              @details="$emit('show-details', card)"
            />
          </slot>
        </div>
      </div>

      <button
        class="carousel-arrow carousel-arrow-right"
        @click="handleArrowClick('next')"
        @mouseenter="startContinuousNavigation('next')"
        @mouseleave="stopContinuousNavigation"
        :disabled="centerIndex === cards.length - 1"
        v-if="cards.length > 1"
      >
        <i class="fas fa-chevron-right"></i>
      </button>
    </div>
  </div>
</template>

<script>
import ServiceCarouselCard from "./ServiceCarouselCard.vue";

export default {
  name: "MagneticCarousel",
  components: {
    ServiceCarouselCard,
  },
  props: {
    cards: {
      type: Array,
      required: true,
      default: () => [],
    },
    initialCenter: {
      type: Number,
      default: null,
    },
  },
  emits: ["toggle-service", "show-details", "card-changed"],
  data() {
    return {
      centerIndex: 0,
      navigationInterval: null,
      transitionDuration: 0.8,
      scrollTimeout: null,
      isScrolling: false,
      touchStartX: 0,
      touchEndX: 0,
    };
  },
  computed: {
    transitionStyle() {
      return `all ${this.transitionDuration}s cubic-bezier(0.25, 0.46, 0.45, 0.94)`;
    },
  },
  watch: {
    cards: {
      handler(newCards) {
        if (newCards.length > 0 && this.centerIndex >= newCards.length) {
          this.centerIndex = Math.floor(newCards.length / 2);
        }
      },
      immediate: true,
    },
  },
  mounted() {
    if (this.initialCenter !== null && this.initialCenter < this.cards.length) {
      this.centerIndex = this.initialCenter;
    } else {
      this.centerIndex = Math.floor(this.cards.length / 2);
    }
  },
  beforeUnmount() {
    this.stopContinuousNavigation();
    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout);
    }
  },
  methods: {
    handleArrowClick(direction) {
      this.stopContinuousNavigation();

      if (direction === "prev") {
        this.navigatePrevious();
      } else if (direction === "next") {
        this.navigateNext();
      }
    },
    startContinuousNavigation(direction) {
      this.stopContinuousNavigation();

      this.navigationInterval = setInterval(() => {
        if (direction === "prev") {
          this.navigatePrevious();
        } else if (direction === "next") {
          this.navigateNext();
        }
      }, 1000);
    },

    stopContinuousNavigation() {
      if (this.navigationInterval) {
        clearInterval(this.navigationInterval);
        this.navigationInterval = null;
      }
    },

    navigatePrevious() {
      if (this.centerIndex > 0) {
        this.centerIndex--;
        if (this.cards[this.centerIndex]) {
          this.$emit("card-changed", this.cards[this.centerIndex]);
        }
      } else {
        this.stopContinuousNavigation();
      }
    },

    navigateNext() {
      if (this.centerIndex < this.cards.length - 1) {
        this.centerIndex++;
        if (this.cards[this.centerIndex]) {
          this.$emit("card-changed", this.cards[this.centerIndex]);
        }
      } else {
        this.stopContinuousNavigation();
      }
    },

    setCenterCard(index) {
      const distance = Math.abs(index - this.centerIndex);
      if (distance > 5) {
        this.transitionDuration = 1.2;
      } else if (distance > 3) {
        this.transitionDuration = 1.0;
      } else {
        this.transitionDuration = 0.8;
      }
      this.centerIndex = index;

      if (this.cards[index]) {
        this.$emit("card-changed", this.cards[index]);
      }
      setTimeout(() => {
        this.transitionDuration = 0.8;
      }, this.transitionDuration * 1000);
    },
    handleScroll(event) {
      const deltaX = event.deltaX;
      const deltaY = event.deltaY;

      if (Math.abs(deltaY) > Math.abs(deltaX)) {
        return;
      }

      if (Math.abs(deltaX) > Math.abs(deltaY)) {
        if (event.cancelable) {
          event.preventDefault();
        }
        event.stopPropagation();

        if (this.isScrolling) return;

        if (Math.abs(deltaX) > 10) {
          this.isScrolling = true;

          if (deltaX > 0) {
            this.navigateNext();
          } else if (deltaX < 0) {
            this.navigatePrevious();
          }

          setTimeout(() => {
            this.isScrolling = false;
          }, this.transitionDuration * 1000);
        }
      }
    },
    handleTouchStart(event) {
      this.touchStartX = event.touches[0].clientX;
    },
    handleTouchMove(event) {
      this.touchEndX = event.touches[0].clientX;
    },
    handleTouchEnd() {
      if (!this.touchStartX || !this.touchEndX) return;

      const difference = this.touchStartX - this.touchEndX;
      const swipeThreshold = 50;

      if (Math.abs(difference) > swipeThreshold) {
        if (difference > 0) {
          this.navigateNext();
        } else {
          this.navigatePrevious();
        }
      }

      this.touchStartX = 0;
      this.touchEndX = 0;
    },
    getCardPosition(index) {
      const diff = index - this.centerIndex;

      if (diff === 0) return "position-center";
      if (diff === -1) return "position-left-1";
      if (diff === -2) return "position-left-2";
      if (diff === 1) return "position-right-1";
      if (diff === 2) return "position-right-2";
      if (diff < -2) return "position-left-hidden";
      if (diff > 2) return "position-right-hidden";

      return "";
    },
  },
};
</script>

<style scoped>
.magnetic-carousel-wrapper {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-wrap: wrap;
}

.carousel-container {
  width: 100%;
  max-width: 1400px;
  height: 450px;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  perspective: 1500px;
  /* overflow: clip avoids creating a stacking context in Firefox,
     which otherwise flattens 3D-transformed children (hiding the center card).
     Fallback to hidden for very old browsers. */
  overflow: hidden;
  overflow: clip;
  transform-style: preserve-3d;
  margin: 0 auto;
}

.carousel-cards-container {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow-x: auto;
  overflow-y: hidden;
  scrollbar-width: none;
  -ms-overflow-style: none;
  transform-style: preserve-3d;
}

.carousel-cards-container::-webkit-scrollbar {
  display: none;
}

.carousel-card-wrapper {
  position: absolute;
  will-change: transform, opacity;
}

.carousel-cards-container .carousel-card-wrapper {
  transition: v-bind(transitionStyle);
}

/* Position Classes */
.position-center {
  transform: translateX(0) scale(1.05) translateZ(0) rotateY(0deg);
  z-index: 50;
  opacity: 1;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.position-left-1 {
  transform: translateX(-320px) scale(0.85) translateZ(-150px) rotateY(15deg);
  z-index: 40;
  opacity: 0.75;
  filter: none;
}

.position-left-2 {
  transform: translateX(-580px) scale(0.7) translateZ(-250px) rotateY(25deg);
  z-index: 30;
  opacity: 0.5;
  filter: none;
}

.position-left-hidden {
  transform: translateX(-800px) scale(0.6) translateZ(-350px) rotateY(35deg);
  z-index: 20;
  opacity: 0.2;
  pointer-events: none;
  filter: none;
}

.position-right-1 {
  transform: translateX(320px) scale(0.85) translateZ(-150px) rotateY(-15deg);
  z-index: 40;
  opacity: 0.75;
  filter: none;
}

.position-right-2 {
  transform: translateX(580px) scale(0.7) translateZ(-250px) rotateY(-25deg);
  z-index: 30;
  opacity: 0.5;
  filter: none;
}

.position-right-hidden {
  transform: translateX(800px) scale(0.6) translateZ(-350px) rotateY(-35deg);
  z-index: 20;
  opacity: 0.2;
  pointer-events: none;
  filter: none;
}

.carousel-card-wrapper.position-center {
  cursor: pointer;
}

.carousel-arrow {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: white;
  border: 2px solid var(--primary-color);
  color: var(--primary-color);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  z-index: 100;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.carousel-arrow:hover:not(:disabled) {
  background: var(--primary-color);
  color: white;
  transform: translateY(-50%) scale(1.1);
  box-shadow: 0 6px 16px rgba(89, 255, 116, 0.3);
}

.carousel-arrow:disabled {
  opacity: 0.3;
  cursor: not-allowed;
  border-color: rgba(108, 117, 125, 0.3);
  color: rgba(108, 117, 125, 0.5);
}

.carousel-arrow i {
  font-size: 1.2rem;
}

.carousel-arrow-left {
  left: calc(50% - 220px);
}

.carousel-arrow-right {
  right: calc(50% - 220px);
}

@media (max-width: 1200px) {
  .carousel-container {
    max-width: 1000px;
  }

  .position-left-1,
  .position-right-1 {
    transform: translateX(-320px) scale(0.85) translateZ(-150px);
  }

  .position-right-1 {
    transform: translateX(320px) scale(0.85) translateZ(-150px);
  }

  .position-left-2,
  .position-right-2 {
    transform: translateX(-550px) scale(0.7) translateZ(-250px);
  }

  .position-right-2 {
    transform: translateX(550px) scale(0.7) translateZ(-250px);
  }
}

@media (max-width: 768px) {
  .carousel-container {
    height: 520px;
    max-width: 100%;
  }

  .carousel-arrow {
    width: 40px;
    height: 40px;
  }

  .carousel-arrow i {
    font-size: 1rem;
  }

  .carousel-arrow-left {
    left: calc(50% - 200px);
  }

  .carousel-arrow-right {
    right: calc(50% - 200px);
  }

  .position-center {
    transform: translateX(0) scale(1) translateZ(0);
  }

  .position-left-1,
  .position-right-1 {
    transform: translateX(-300px) scale(0.75) translateZ(-100px);
  }

  .position-right-1 {
    transform: translateX(300px) scale(0.75) translateZ(-100px);
  }

  .position-left-2,
  .position-right-2,
  .position-left-hidden,
  .position-right-hidden {
    opacity: 0;
    pointer-events: none;
  }
}

@media (max-width: 480px) {
  .magnetic-carousel-wrapper {
    padding: 20px 10px;
  }

  .carousel-container {
    height: 460px;
  }

  .position-left-1,
  .position-right-1 {
    opacity: 0;
    pointer-events: none;
  }
}
</style>
